import type { LinkProps } from '@hover/blueprint';
import {
  Box,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
} from '@hover/blueprint';
import React from 'react';
import { generatePath } from 'react-router';

import { useFeatureAccesses } from 'src/modules/propertyDetails/hooks/useFeatureAccesses';
import { useHasAccessForOrg } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import { Image, Link as SharedLink } from 'src/shared/components';
import {
  AclTemplateOptions,
  FeatureAccessIdentifier,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useBrand } from 'src/shared/providers/Brand';
import { External } from 'src/shared/route';

const NavLink = ({ ...props }: LinkProps) => (
  <Link
    _hover={{ color: 'white' }}
    color="neutral.0"
    fontSize={200}
    justifyContent="center"
    margin="0.5rem"
    padding="1.7rem 0.5rem 1.4rem"
    textTransform="uppercase"
    {...props}
  />
);

const NavBar = ({ onLogout }: { onLogout: () => void }) => {
  const t = useTranslate(TranslationNamespace.APP);
  const tCommon = useTranslate(TranslationNamespace.COMMON);
  const { data } = useProfileQuery();
  const {
    isEstimationEnabled,
    isMaterialListEnabled,
    isInspectionChecklistEnabled,
  } = useFeatureAccesses();
  const [isTeamAdmin] = useHasAccessForOrg([AclTemplateOptions.Admin]);
  const [isJobManager] = useHasAccessForOrg([AclTemplateOptions.JobManager]);

  const user = data?.profile?.user;
  const org = user?.orgs[0];

  const featureAccesses = org?.featureAccesses;
  const hasEstimationAccess =
    featureAccesses &&
    featureAccesses.some(
      v => v.identifier === FeatureAccessIdentifier.SalesEstimation,
    );

  const { logo, primaryColor, secondaryColor, loadingBrand } = useBrand();

  const canManageEhiProjects = isTeamAdmin || isJobManager;

  const showWorkflows =
    isMaterialListEnabled ||
    ((isEstimationEnabled || isInspectionChecklistEnabled) &&
      canManageEhiProjects);

  return loadingBrand ? null : (
    <Box
      alignItems="center"
      as="header"
      backgroundColor={primaryColor}
      height="66px"
      justifyContent="space-between"
      justifyItems="center"
      paddingLeft="20px"
      paddingRight="15px"
      transition="background 800ms"
    >
      <Box>
        <SharedLink
          aria-label={t('navigation.navigateToHome')}
          isExternal
          target="_self"
          testId="hover-logo"
          to={External.START.path}
        >
          <Box flexDirection="row" justifyContent="center" minW={500}>
            <Image
              alt={t('logoWithText', { name: 'HOVER' })}
              height={200}
              paddingRight={400}
              paddingTop={100}
              src={logo}
            />
          </Box>
        </SharedLink>

        <NavLink href={External.START.path}>
          {tCommon('navigation.myProperties')}
        </NavLink>
        {isTeamAdmin && (
          <NavLink
            href={generatePath(External.MANAGE_TEAM.path, {
              orgId: org?.id as number,
            })}
          >
            {tCommon('navigation.manageTeam')}
          </NavLink>
        )}
        {isEstimationEnabled && hasEstimationAccess && (
          <NavLink href={External.PROJECT_LIST.path}>
            {tCommon('navigation.projectsList')}
          </NavLink>
        )}
      </Box>
      <Menu
        trigger={
          <MenuButton backgroundColor={secondaryColor} fontWeight={300}>
            {user?.email}
          </MenuButton>
        }
      >
        {showWorkflows && (
          <MenuItem
            onClick={() => window.open(External.MANAGE_WORKFLOWS.path, '_self')}
          >
            {tCommon('navigation.manageWorkflows')}
          </MenuItem>
        )}
        {isTeamAdmin && (
          <MenuItem onClick={() => window.open(External.PLAN.path, '_self')}>
            {tCommon('navigation.planAndBilling')}
          </MenuItem>
        )}
        <MenuItem onClick={() => window.open(External.SETTINGS.path, '_self')}>
          {tCommon('navigation.settings')}
        </MenuItem>
        <MenuDivider />
        <MenuItem onClick={onLogout}>{tCommon('logOut')}</MenuItem>
      </Menu>
    </Box>
  );
};

export { NavBar };
